import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { 
  School, 
  LogOut, 
  FileText, 
  Download, 
  Eye, 
  User, 
  GraduationCap,
  AlertCircle,
  CheckCircle,
  Clock,
  Lock,
  Settings,
  Award
} from 'lucide-react';
import apiService from '@/services/api';
import { ParentCertificateCard } from '@/components/parent/ParentCertificateCard';

export default function ParentDashboard() {
  const navigate = useNavigate();
  const [parentData, setParentData] = useState(null);
  const [studentData, setStudentData] = useState(null);
  const [reportCards, setReportCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('parentToken');
    const parent = localStorage.getItem('parentData');
    const student = localStorage.getItem('studentData');

    if (!token || !student) {
      navigate('/parent/login');
      return;
    }

    // parentData is optional (old phone/PIN login)
    // studentData is required (new Student ID + DOB login)
    if (parent) {
      setParentData(JSON.parse(parent));
    }
    setStudentData(JSON.parse(student));

    // Fetch available report cards
    fetchReportCards(token);
  }, [navigate]);

  const fetchReportCards = async (token) => {
    try {
      setLoading(true);
      const response = await apiService.getParentReportCards(token);
      setReportCards(response.published_reports || []);
    } catch (err) {
      console.error('Error fetching report cards:', err);
      setError('Failed to load report cards. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('parentToken');
    localStorage.removeItem('parentData');
    localStorage.removeItem('studentData');
    navigate('/parent/login');
  };

  const handleViewReport = (report) => {
    // Navigate to report viewer with report details
    navigate(`/parent/report/${report.year}/${report.term}`);
  };

  const handleChangePIN = () => {
    navigate('/parent/change-pin');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-md sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <School className="h-8 w-8 text-blue-600" />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Parent Portal</h1>
                <p className="text-xs text-gray-600">Penama Education Portal</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              variant="outline"
              size="sm"
              className="gap-2"
            >
              <LogOut className="h-4 w-4" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Welcome Card */}
        <Card className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-xl">
          <CardHeader>
            <CardTitle className="text-2xl">
              Welcome{parentData?.parent_name ? `, ${parentData.parent_name}` : ''}!
            </CardTitle>
            <CardDescription className="text-blue-100">
              View {studentData?.name ? `${studentData.name}'s` : "your child's"} academic progress
            </CardDescription>
          </CardHeader>
        </Card>

        {/* Student Information */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5 text-blue-600" />
              Student Information
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-gray-600">Student Name</p>
                <p className="font-bold text-lg">{studentData?.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Year Level</p>
                <p className="font-bold text-lg">Year {studentData?.year || 'N/A'}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Stream</p>
                <Badge className="mt-1">
                  {studentData?.stream || 'Not Assigned'}
                </Badge>
              </div>
              <div>
                <p className="text-sm text-gray-600">Relationship</p>
                <p className="font-medium">{parentData?.relationship || 'Guardian'}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {reportCards.length > 0 && <ParentCertificateCard token={localStorage.getItem('parentToken')} studentName={studentData?.name} />}

        {/* Report Cards Section */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileText className="h-5 w-5 text-blue-600" />
                Report Cards
              </CardTitle>
              <Badge variant="outline">
                {reportCards.length} Available
              </Badge>
            </div>
            <CardDescription>
              View and download your child's term report cards
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-6">
            {reportCards.length === 0 ? (
              <div className="text-center py-12">
                <Clock className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 font-medium mb-2">No Reports Available Yet</p>
                <p className="text-sm text-gray-500">
                  Report cards will appear here when published by the school
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {reportCards.map((report) => (
                  <Card key={report.id} className="border-2 hover:border-blue-300 transition-colors">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <GraduationCap className="h-5 w-5 text-blue-600" />
                            <h3 className="font-bold text-lg">
                              {report.term} - Year {report.year}
                            </h3>
                            <Badge className="bg-green-600">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Published
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600">
                            Published: {new Date(report.published_at).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleViewReport(report)}
                            className="gap-2"
                          >
                            <Eye className="h-4 w-4" />
                            View
                          </Button>
                          <Button
                            variant="outline"
                            className="gap-2"
                            onClick={() => handleViewReport(report)}
                          >
                            <Download className="h-4 w-4" />
                            Download
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Settings Card */}
        <Card className="shadow-lg">
          <CardHeader className="bg-gray-50">
            <CardTitle className="flex items-center gap-2">
              <Settings className="h-5 w-5 text-blue-600" />
              Account Settings
            </CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <Button
              onClick={handleChangePIN}
              variant="outline"
              className="w-full gap-2"
            >
              <Lock className="h-4 w-4" />
              Change PIN
            </Button>
          </CardContent>
        </Card>
      </main>

      {/* Footer */}
      <footer className="text-center py-6 text-sm text-gray-600">
        <p>Academic Year 2025-2026</p>
        <p className="text-xs mt-1">© Penama Education Portal, Vanuatu</p>
      </footer>
    </div>
  );
}
